import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import SearchFilter from 'components/common/forms/SearchFilter';
import TableForm from 'components/common/lists/TableForm';
import styles from 'styles/components/pages/student/patientDetail/ChartTable.module.scss';
import 'swiper/css';
import 'swiper/css/pagination';
import { ChartInfo } from 'constants/chart/common/chartInfo';
import { TTableColumn } from 'types/components/table';
import {
  EvaluationChartTableFormLabel,
  PatientDetailSortByFormLabel,
} from 'types/form/evaluationChart';
import {
  EvaluationChartTable,
  PatientDetailEvaluationChartSortBy,
  TEvaluationChartByPatientTableInfo,
} from 'types/apis/chart/studentEvaluationChart';
import { getEvaluationChartListByPatient } from 'apis/chart/studentEvaluationChart';
import { SortDirection } from 'types/apis/common';
import useModal from 'hooks/useModal';

const tableColumns: TTableColumn[] = [
  {
    name: 'no',
    label: '번호',
    width: '80px',
  },
  {
    name: EvaluationChartTable.RegisteredChartName,
    label: EvaluationChartTableFormLabel.RegisteredChartName,
    width: '400px',
  },
  {
    name: EvaluationChartTable.ChartTypeName,
    label: EvaluationChartTableFormLabel.ChartTypeName,
    width: '240px',
  },
  {
    name: EvaluationChartTable.LastModifiedAt,
    label: EvaluationChartTableFormLabel.LastModifiedAt,
    width: '150px',
    onClick: () => {
      return;
    },
  },
  {
    name: EvaluationChartTable.EvaluationAvgScore,
    label: EvaluationChartTableFormLabel.EvaluationAvgScore,
    width: '150px',
    onClick: () => {
      return;
    },
  },
];

const ChartTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { id } = useParams();
  const patientId = id as string;

  const { openModal } = useModal();

  const [params, setParams] = useState({
    courseInfoId: Number(query.get('courseInfoId')),
    chartRound: Number(query.get('chartRound')),
    sortBy:
      (query.get('sortBy') as PatientDetailEvaluationChartSortBy) || undefined,
    isAsc:
      query.get('isAsc') === SortDirection.Asc
        ? true
        : query.get('sort') === SortDirection.Desc
        ? false
        : undefined,
  });
  const [sortBy, setSortBy] = useState(query.get('sortBy') || '');
  const [sortDirection, setSortDirection] = useState(query.get('sort') || '');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (sortBy !== query.get('sortBy')) setSortBy('');
    if (sortDirection !== query.get('sort')) setSortDirection('');
    setParams({
      ...params,
      courseInfoId: Number(query.get('courseInfoId')),
      chartRound: Number(query.get('chartRound')),
    });
  }, [location.search]);

  const { data: evaluationChartListData, isFetching } = useQuery(
    ['getEvaluationChartListByPatient', params],
    () => getEvaluationChartListByPatient(patientId, params),
    {
      enabled: !!params.courseInfoId && !!params.chartRound,
    }
  );

  const tableData: TEvaluationChartByPatientTableInfo[] =
    evaluationChartListData?.data.evaluationChartList || [];

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  const onClickSort = (label: string) => {
    const sortAsc =
      sortDirection === ''
        ? SortDirection.Asc
        : sortDirection === SortDirection.Asc
        ? SortDirection.Desc
        : '';
    const sortBy = {
      [PatientDetailSortByFormLabel.LastModifiedAt]:
        PatientDetailEvaluationChartSortBy.LastModifiedAt,
      [PatientDetailSortByFormLabel.EvaluationAvgScore]:
        PatientDetailEvaluationChartSortBy.Score,
    }[label as PatientDetailSortByFormLabel];

    setSortDirection(sortAsc);
    setSortBy(sortBy);

    if (!sortAsc) {
      query.delete('sort');
      query.delete('sortBy');
    } else {
      query.set('sort', sortAsc);
      query.set('sortBy', sortBy);
    }
    navigate({
      search: query.toString(),
    });

    setParams({
      ...params,
      sortBy:
        (query.get('sortBy') as PatientDetailEvaluationChartSortBy) ||
        undefined,
      isAsc:
        sortAsc === SortDirection.Asc
          ? true
          : sortAsc === SortDirection.Desc
          ? false
          : undefined,
    });
  };

  const onClickRow = (row: TEvaluationChartByPatientTableInfo) => {
    const chartRoute = ChartInfo.find(
      (chart) => chart.type === row.chartTypeNum
    )?.route;
    if (!chartRoute || !row.evaluationChartId || !patientId) {
      openModal({
        title: '차트 조회 실패',
        text: '관리자에게 문의해 주세요.',
      });
      return;
    }
    sessionStorage.setItem('route', 'patient');
    // 차트 상세 페이지로 이동
    const hasScore =
      row.evaluationAvgScore !== '-' && row.evaluationAvgScore !== null;
    navigate(
      `/evaluation${chartRoute}/${
        row.evaluationChartId
      }?patientId=${patientId}&courseInfoId=${params.courseInfoId}&chartRound=${
        params.chartRound
      }&tab=${
        row.evaluationRound === 2 ? (hasScore ? 3 : 2) : hasScore ? 1 : 0
      }`
    );
  };

  return (
    <>
      <div className="flex mt32 mb16 justify_between">
        <div className="flex">
          <div className={styles.tab_active}>
            <span className={styles.text}>실습용 차트</span>
          </div>
        </div>

        <div className="flex items_center">
          <SearchFilter
            isInnerItem={true}
            dropdown={[
              {
                id: 'courseClassInfo',
                placeholder: '강의명',
                width: '240px',
              },
              {
                id: 'chartRoundByPatient',
                placeholder: '(회차) 회차명',
                width: '240px',
              },
            ]}
            align="right"
          />
        </div>
      </div>

      <TableForm
        data={tableData}
        columns={tableColumns}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onClickSort={onClickSort}
        onClickRow={onClickRow}
        idField="evaluationChartId"
        emptyMessage="등록된 실습용 차트가 없습니다."
        isLoading={isLoading}
      />
    </>
  );
};

export default ChartTable;
